import {
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from "react";

import { atom, createStore, useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";

import { type TabId } from "../tabs";

interface Props {
  maxIndentLevel: number;
}

const MaxIndentLevelContext = createContext(0);

export const MaxIndentLevelProvider = ({
  maxIndentLevel,
  children,
}: PropsWithChildren<Props>) => (
  <MaxIndentLevelContext.Provider value={maxIndentLevel}>
    {children}
  </MaxIndentLevelContext.Provider>
);

export const useMaxIndentLevel = () => {
  const context = useContext(MaxIndentLevelContext);

  if (context === undefined) {
    throw new Error(
      "useMaxIndentLevel must be used within a MaxIndentLevelProvider",
    );
  }

  return context;
};

export const salesV3Scope = "sales-v3";

const SalesTableV3Context = createContext<ReturnType<
  typeof createStore
> | null>(null);
const useSalesTableV3Context = () => {
  const context = useContext(SalesTableV3Context);
  if (!context) {
    throw new Error("Sales V3 context not available");
  }
  return context;
};
const store = createStore();

export const SalesV3Provider = ({ children }: PropsWithChildren<unknown>) => {
  return (
    <SalesTableV3Context.Provider value={store}>
      {children}
    </SalesTableV3Context.Provider>
  );
};

type ExpandedRowIds = string[];
const expandedRowIdsAtom = atom<ExpandedRowIds>([]);
export const useExpandedRowIds = () => {
  const store = useSalesTableV3Context();
  return useAtom(expandedRowIdsAtom, { store });
};

const doNotShowCharts = atomWithStorage("doNotShowCharts", false);
export const useDoNotShowCharts = () => {
  const store = useSalesTableV3Context();
  return useAtom(doNotShowCharts, { store });
};

const breakdownTopPlatformsAtom = atom<string[]>([]);
const campaignTopPlatformsAtom = atom<string[]>([]);
const adsetTopPlatformsAtom = atom<string[]>([]);
const adTopPlatformsAtom = atom<string[]>([]);
export const useTabTopPlatforms = (tabId: TabId) => {
  const store = useSalesTableV3Context();

  const breakdownTopPlatforms = useAtom(breakdownTopPlatformsAtom, { store });
  const campaignTopPlatforms = useAtom(campaignTopPlatformsAtom, { store });
  const adsetTopPlatforms = useAtom(adsetTopPlatformsAtom, { store });
  const adTopPlatforms = useAtom(adTopPlatformsAtom, { store });

  const topPlatforms = useMemo(
    () => ({
      breakdown: breakdownTopPlatforms,
      campaign: campaignTopPlatforms,
      adset: adsetTopPlatforms,
      ad: adTopPlatforms,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return topPlatforms[tabId];
};
